import './App.css';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { lazy, Suspense, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from './contexts/ThemeContext';
import { LocaleProvider } from './contexts/LocaleContext';
import Home from './pages/ALLJsx/Home';
import Inventory from './pages/ALLJsx/Inventory';
import CarDetails from './pages/ALLJsx/CarDetails';
import AppointmentBooking from './pages/ALLJsx/AppointmentBooking';
import Appointments from './pages/ALLJsx/Appointments';
import Wishlist from './pages/ALLJsx/Wishlist';
import SavedSearches from './pages/ALLJsx/SavedSearches';
import Profile from './pages/ALLJsx/Profile';
import Auth from './pages/ALLJsx/Auth';
import Dashboard from './pages/ALLJsx/Dashboard';
import Contact from './pages/ALLJsx/Contact';
import FaqSupport from './pages/ALLJsx/FaqSupport';
import FaqSupportCategory from './pages/ALLJsx/FaqSupportCategory';
import FaqSupportArticle from './pages/ALLJsx/FaqSupportArticle';
import Careers from './pages/ALLJsx/Careers';
import Finance from './pages/ALLJsx/Finance';
import FinanceApply from './pages/ALLJsx/FinanceApply';
import SellTrade from './pages/ALLJsx/SellTrade';
import SellACar from './pages/ALLJsx/SellACar';
import PrivacyPolicy from './pages/ALLJsx/PrivacyPolicy';
import TermsOfService from './pages/ALLJsx/TermsOfService';
import VehicleSourcingFeeAgreement from './pages/ALLJsx/VehicleSourcingFeeAgreement';
import CookiesPolicy from './pages/ALLJsx/CookiesPolicy';
import NotFound from './pages/ALLJsx/NotFound';
import InfoPage from './pages/ALLJsx/InfoPage';
import SatisfactionSurveyPage from './pages/ALLJsx/SatisfactionSurveyPage';
import api from './services/api';
import { setWishlist } from './features/wishlist/wishlistSlice';
import { getWishlist } from './services/wishlistService';
import { restoreAuth, logout } from './features/auth/authSlice';
import { handleSessionExpired } from './utils/sessionExpiry';
import ChatBox from './components/ALLJsx/ChatBox';
import CookieConsent from './components/ALLJsx/CookieConsent';
import SkipToContent from './components/ALLJsx/SkipToContent';
import BackToTop from './components/ALLJsx/BackToTop';
import { loadAnalyticsIfConsented } from './utils/analyticsConsent';
import { trackWebsiteVisit } from './utils/websiteVisitTracker';
import { ToastProvider } from './contexts/ToastContext';
import { ConfirmProvider } from './contexts/ConfirmContext';
import ToastBridge from './components/ALLJsx/ToastBridge';
import ErrorBoundary from './components/ALLJsx/ErrorBoundary';
import { usePrefersReducedMotion, getScrollBehavior } from './hooks/usePrefersReducedMotion';
const SuperAdminLogin = lazy(() => import('./superadmin/pages/ALLJsx/SuperAdminLogin'));
const SuperAdminDashboard = lazy(() => import('./superadmin/pages/ALLJsx/SuperAdminDashboard'));
const PartnerDealersAdmin = lazy(() => import('./superadmin/pages/ALLJsx/PartnerDealersAdmin'));
const PartnerRegister = lazy(() => import('./partner/pages/ALLJsx/PartnerRegister'));
const PartnerLogin = lazy(() => import('./partner/pages/ALLJsx/PartnerLogin'));
const PartnerDashboard = lazy(() => import('./partner/pages/ALLJsx/PartnerDashboard'));
const Reports = lazy(() => import('./superadmin/pages/ALLJsx/Reports'));
const StatsByDateReport = lazy(() => import('./superadmin/pages/ALLJsx/StatsByDateReport'));
const BackupRestore = lazy(() => import('./superadmin/pages/ALLJsx/BackupRestore'));
const CustomerSatisfactionSurveys = lazy(() => import('./superadmin/pages/ALLJsx/CustomerSatisfactionSurveys'));
const CarForm = lazy(() => import('./superadmin/pages/ALLJsx/CarForm'));
const CommandCenter = lazy(() => import('./superadmin/pages/ALLJsx/CommandCenter'));

function RequireAuth({ children }) {
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  return children;
}

function LegacyCarRedirect() {
  const { id } = useParams();
  return <Navigate to={`/cars/${id}`} replace />;
}

function RouteFallback() {
  return (
    <div className="route-loading" role="status" aria-live="polite" style={{ padding: '3rem', textAlign: 'center' }}>
      Loading…
    </div>
  );
}

/** Scroll to top on every route change (navbar, footer, in-app links). */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const reducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: getScrollBehavior(reducedMotion) });
  }, [pathname, hash, reducedMotion]);

  return null;
}

/** POST /api/website-visits/track once per tab session on public pages. */
function WebsiteVisitTracker() {
  const { pathname } = useLocation();

  useEffect(() => {
    trackWebsiteVisit(pathname);
  }, [pathname]);

  return null;
}

const CHAT_ALLOWED_PREFIXES = ['/', '/inventory', '/cars/', '/contact', '/finance', '/get-financed', '/wishlist', '/faq-support'];

function shouldShowChat(pathname) {
  if (pathname.startsWith('/superadmin')) return false;
  if (pathname.startsWith('/partner')) return false;
  return CHAT_ALLOWED_PREFIXES.some(
    (p) => p === pathname || (p !== '/' && pathname.startsWith(p))
  );
}

function LayoutWrapper() {
  const location = useLocation();
  const isSuperAdmin = location.pathname.startsWith('/superadmin');
  const showChat = useMemo(() => shouldShowChat(location.pathname), [location.pathname]);

  return (
    <>
      <SkipToContent />
      <ScrollToTop />
      <WebsiteVisitTracker />
      <main id="main-content" className="App-content" tabIndex={-1}>
        <ErrorBoundary>
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          {/* Super Admin Routes - MUST be before other routes */}
          <Route path="/superadmin/login" element={<SuperAdminLogin />} />
          <Route path="/superadmin/dashboard" element={<Navigate to="/superadmin/overview" replace />} />
          <Route path="/superadmin/overview" element={<SuperAdminDashboard />} />
          <Route path="/superadmin/inventory" element={<SuperAdminDashboard />} />
          <Route path="/superadmin/sold" element={<SuperAdminDashboard />} />
          <Route path="/superadmin/employee" element={<SuperAdminDashboard />} />
          <Route path="/superadmin/customers" element={<Navigate to="/superadmin/overview" replace />} />
          <Route path="/superadmin/appointments" element={<SuperAdminDashboard />} />
          <Route path="/superadmin/interested-in" element={<SuperAdminDashboard />} />
          <Route path="/superadmin/walk-ins" element={<SuperAdminDashboard />} />
          <Route path="/superadmin/chat" element={<SuperAdminDashboard />} />
          <Route path="/superadmin/expenses" element={<SuperAdminDashboard />} />
          <Route path="/superadmin/offices" element={<SuperAdminDashboard />} />
          <Route path="/superadmin/reports" element={<Reports />} />
          <Route path="/superadmin/reports/statsbydate" element={<StatsByDateReport />} />
          <Route path="/superadmin/backup" element={<BackupRestore />} />
          <Route path="/superadmin/surveys" element={<CustomerSatisfactionSurveys />} />
          <Route path="/superadmin/command-center" element={<Navigate to="/superadmin/command-center/crm" replace />} />
          <Route path="/superadmin/command-center/:tab" element={<CommandCenter />} />
          <Route path="/superadmin/cars/add" element={<CarForm />} />
          <Route path="/superadmin/cars/edit/:id" element={<CarForm />} />
          <Route path="/superadmin/partner-dealers" element={<PartnerDealersAdmin />} />

          {/* Partner Dealer Portal */}
          <Route path="/partner/register" element={<PartnerRegister />} />
          <Route path="/partner/login" element={<PartnerLogin />} />
          <Route path="/partner/dashboard" element={<PartnerDashboard />} />
          
          {/* Regular Routes */}
          <Route path="/" element={<Home />} />
          <Route path="/home-stats" element={<Navigate to="/" replace />} />
          <Route path="/inventory" element={<Inventory />} />
          <Route path="/car/:id" element={<LegacyCarRedirect />} />
          <Route path="/cars/:id" element={<CarDetails />} />
          <Route path="/survey/:surveyType" element={<SatisfactionSurveyPage />} />
          <Route path="/appointment/:stockNumber" element={<AppointmentBooking />} />
          <Route
            path="/appointments"
            element={
              <RequireAuth>
                <Appointments />
              </RequireAuth>
            }
          />
          <Route
            path="/wishlist"
            element={
              <RequireAuth>
                <Wishlist />
              </RequireAuth>
            }
          />
          <Route
            path="/saved-searches"
            element={
              <RequireAuth>
                <SavedSearches />
              </RequireAuth>
            }
          />
          <Route
            path="/profile"
            element={
              <RequireAuth>
                <Profile />
              </RequireAuth>
            }
          />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq-support" element={<FaqSupport />} />
          <Route path="/faq-support/:categoryId" element={<FaqSupportCategory />} />
          <Route path="/faq-support/:categoryId/:questionId" element={<FaqSupportArticle />} />
          <Route path="/about" element={<InfoPage pageKey="about" />} />
          <Route path="/blog" element={<InfoPage pageKey="blog" />} />
          <Route path="/faq" element={<Navigate to="/faq-support" replace />} />
          <Route path="/shipping" element={<InfoPage pageKey="shipping" />} />
          <Route path="/warranty" element={<InfoPage pageKey="warranty" />} />
          <Route path="/returns" element={<InfoPage pageKey="returns" />} />
          <Route path="/careers" element={<Careers />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/vehicle-sourcing-fee-agreement" element={<VehicleSourcingFeeAgreement />} />
          <Route path="/cookies-policy" element={<CookiesPolicy />} />
          <Route path="/finance" element={<Finance />} />
          <Route path="/get-financed" element={<FinanceApply />} />
          <Route path="/sell" element={<SellACar />} />
          <Route path="/sell/trade" element={<SellTrade />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/register" element={<Auth />} />
          <Route
            path="/dashboard"
            element={
              <RequireAuth>
                <Dashboard />
              </RequireAuth>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        </ErrorBoundary>
        {showChat && <ChatBox />}
      </main>
      {!isSuperAdmin && <BackToTop />}
    </>
  );
}

function App() {
  const dispatch = useDispatch();

  useEffect(() => {
    // Restore authentication state from localStorage
    const restoreAuthState = async () => {
      const storedUser = localStorage.getItem('user');

      if (storedUser) {
        try {
          const cachedUser = JSON.parse(storedUser);
          dispatch(restoreAuth({ user: cachedUser }));
        } catch (parseError) {
          console.error('Error parsing stored user:', parseError);
          localStorage.removeItem('user');
        }
      }

      try {
        const response = await api.get('/auth/profile');
        if (response.data.success) {
          const user = response.data.data.user;
          dispatch(restoreAuth({ user }));
          localStorage.setItem('user', JSON.stringify(user));

          try {
            const wishlistIds = await getWishlist();
            dispatch(setWishlist(wishlistIds));
          } catch (error) {
            console.error('Error loading wishlist:', error);
          }
        }
      } catch (error) {
        if (error.response?.status === 401) {
          handleSessionExpired();
        } else {
          localStorage.removeItem('user');
          dispatch(logout());
        }
      }
    };

    restoreAuthState();
    loadAnalyticsIfConsented();
  }, [dispatch]);

  return (
    <ThemeProvider>
      <LocaleProvider>
        <ToastProvider>
          <ConfirmProvider>
            <ToastBridge />
            <div className="App">
              <Routes>
                <Route path="*" element={<LayoutWrapper />} />
              </Routes>
              <CookieConsent />
            </div>
          </ConfirmProvider>
        </ToastProvider>
      </LocaleProvider>
    </ThemeProvider>
  );
}

export default App;
